import React from 'react'

import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import {
  categoryTimeTypeLabel,
  categoryTimeTypeTag,
} from 'modules/attendance/components/CustomTimeRanges/Content/utils/categoryNameWithTimeTypeLabel'
import {
  CustomTimeRangeCategory,
  useFetchCustomTimeRangeCategories,
} from 'modules/attendance/hooks/useFetchCustomTimeRangeCategories'
import { useFetchCustomTimeRanges } from 'modules/attendance/hooks/useFetchCustomTimeRanges'
import { CustomTimeRangesFormData } from 'modules/attendance/lib/forms/utils/CustomTimeRangesFormData'

interface CustomCategorySelectorProps {
  form: FormikProps<CustomTimeRangesFormData>
  selectedCategory?: CustomTimeRangeCategory
  settingId: string
}

const CustomCategorySelector: React.FC<CustomCategorySelectorProps> = ({
  form,
  selectedCategory,
  settingId,
}) => {
  const { categories: customTimeRangeCategories } = useFetchCustomTimeRangeCategories(settingId)
  const { ranges: customTimeRanges } = useFetchCustomTimeRanges(settingId)

  const categoriesWithTimeSettingCategoryArchived =
    customTimeRangeCategories.withTimeSettingCategoryArchived
  const categoriesWithTimeSettingCategoryActive =
    customTimeRangeCategories.withTimeSettingCategoryActive

  const attendanceCategoryIdsFromCustomRanges = new Set(
    customTimeRanges.customTimeRanges.map(
      (timeRange) => timeRange.attendanceCustomTimeRangeCategoryId
    )
  )
  const archivedCategoriesWithTimeBlocks = categoriesWithTimeSettingCategoryArchived.filter(
    (category) => attendanceCategoryIdsFromCustomRanges.has(category.id)
  )

  const categoriesOptions = [
    ...archivedCategoriesWithTimeBlocks,
    ...categoriesWithTimeSettingCategoryActive,
  ].map((category) => {
    const tagLabel = categoryTimeTypeLabel(category.timeType)

    return (
      <Selector.Option
        key={category.name}
        value={category.id.toString()}
        label={category.name}
        searchValue={category.name}
      >
        <Box flexDirection="row" gap="s4" width="full" justifyContent="spaceBetween">
          <Text.Normal ellipsis weight="medium">
            {category.name}
          </Text.Normal>
          <Text.Normal whiteSpace="nowrap">{`${tagLabel} ･ ${category.equivalentMinutes}`}</Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  return (
    <Box gap="s8">
      <Selector.Popover
        label={i18n.t('time_tracking.settings.time_blocks.categories.modals.category')}
        placeholder={i18n.t('common.select')}
        blankLabel={i18n.t('common.select')}
        value={[form.values.attendance_custom_time_range_category_id]}
        onChange={(value) => form.setFieldValue('attendance_custom_time_range_category_id', value)}
        mandatory
        error={form.errors.attendance_custom_time_range_category_id}
        hasSearch={false}
      >
        {categoriesOptions}
      </Selector.Popover>
      {selectedCategory && (
        <Box flexDirection="row" gap="s8">
          <Tag type={categoryTimeTypeTag(selectedCategory.timeType)} inline>
            {categoryTimeTypeLabel(selectedCategory.timeType)}
          </Tag>
          <Text.Normal>{`${selectedCategory.rawMinutes} = ${selectedCategory.equivalentMinutes}`}</Text.Normal>
        </Box>
      )}
    </Box>
  )
}

export default CustomCategorySelector
